// "Params to override" (spec/dashboard.md sections 8.3–8.4): the patchable leaves of a document with checkboxes, All / None,
// the type and the current value beside each; the caller stages the rows.
import { useMemo, useState } from 'react';
import type { Document, Json } from '../../api/types';
import { EmptyState } from '../../ui/Banner';
import { Button } from '../../ui/Button';
import { SlidersHorizontal } from '../../ui/icons';
import { Checkbox } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { useContent } from '../ContentContext';
import { formatValue } from '../format';
import { overridableLeaves, valueAt, type OverridableLeaf } from './model';
import '../../pages/liveops/liveops.css';

export function ParameterPickDialog({ doc, title, confirmLabel, onPick, onClose, exclude = [] }: { doc: Document; title: string; confirmLabel: string; onPick: (leaves: OverridableLeaf[]) => void; onClose: () => void; exclude?: string[] }) {
  const catalog = useContent();
  const template = catalog.templates.get(doc.template);
  const leaves = useMemo(() => (template ? overridableLeaves(catalog, template).filter((l) => !exclude.includes(l.path.toLowerCase())) : []), [catalog, template, exclude]);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const toggle = (path: string) =>
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  return (
    <Modal
      title={`${title} — ${catalog.label(doc)}`}
      open
      onClose={onClose}
      size="m"
      footer={
        <>
          <Button size="s" variant="ghost" onClick={() => setPicked(new Set(leaves.map((l) => l.path)))} disabled={leaves.length === 0}>
            All
          </Button>
          <Button size="s" variant="ghost" onClick={() => setPicked(new Set())} disabled={picked.size === 0}>
            None
          </Button>
          <span className="lo-pick-count" aria-live="polite">
            {picked.size} of {leaves.length} selected
          </span>
          <span className="spacer" />
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={picked.size === 0} onClick={() => onPick(leaves.filter((l) => picked.has(l.path)))}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {leaves.length === 0 ? (
        <EmptyState icon={<SlidersHorizontal aria-hidden="true" />} title="Nothing to override" body="This document has no parameter the engine can patch (objects, lists of objects, scripts and profile data are not overridable)." />
      ) : (
        <>
          <p className="lo-pick-head">Pick the parameters to patch; each becomes a row with the current value as its starting point.</p>
          <ul className="param-pick lo-pick" aria-label="Parameters to override">
            {leaves.map((l) => (
              <li key={l.path} className={picked.has(l.path) ? 'picked' : ''}>
                <Checkbox label={l.label} checked={picked.has(l.path)} onChange={() => toggle(l.path)} />
                <span className="lo-pick-type">{l.param.type}</span>
                <span className="lo-pick-value" title="The current value">
                  {formatValue(l.param, valueAt(doc.data as Json, l.path) as Json | undefined, catalog.format)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Modal>
  );
}
